/**
 * Shared route-guard components for micro-frontend zone protection.
 *
 * Provides three composable guards that wrap route trees:
 *
 * - {@link AuthGuard}    — requires a valid JWT session; redirects
 *   unauthenticated visitors to `/login`.
 * - {@link ProfileGuard} — requires a complete profile (name + birthday);
 *   redirects to `/onboard/profile` with a `returnTo` parameter so the
 *   user is sent back to the originally-requested URL after completion.
 * - {@link ScopeGuard}   — requires a specific {@link AuthScope};
 *   redirects users to their allowed zone (or `/onboard`) when the
 *   required scope is missing.
 *
 * All three guards render a centered spinner while the initial auth
 * check is in-flight, so downstream components can assume auth state is
 * resolved by the time they mount.
 *
 * Cross-zone redirects use `window.location.href` because each zone
 * is an independent Vite application — SPA routing cannot cross zone
 * boundaries.
 *
 * @module lib/auth-guard
 */

import { type AuthScope, defaultRedirectForScopes } from '@api/lib/auth';
import { useAuth } from '@api/lib/auth-provider';
import { Spinner } from '@learnai/ui/components/ui/spinner';
import { useEffect } from 'react';

// ---------------------------------------------------------------------------
// Safe returnTo
// ---------------------------------------------------------------------------

/**
 * Validates a `returnTo` path — must be a relative, same-origin path
 * starting with `/` (and not `//`, which would be protocol-relative).
 * Returns the original value when safe, or `null` otherwise.
 *
 * Used by all guards to prevent open-redirect attacks when redirecting
 * the user back to their originally-requested URL after authentication
 * or profile completion.
 */
function safeReturnTo(value: string | null | undefined): string | null {
	if (!value) return null;
	if (value.startsWith('/') && !value.startsWith('//')) return value;
	return null;
}

// ---------------------------------------------------------------------------
// Loading screen
// ---------------------------------------------------------------------------

function AuthLoading() {
	return (
		<div className="flex min-h-screen items-center justify-center">
			<div className="flex flex-col items-center gap-4">
				<Spinner className="size-6 text-muted-foreground" />
				<p className="text-sm text-muted-foreground">Checking authentication…</p>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// AuthGuard
// ---------------------------------------------------------------------------

interface AuthGuardProps {
	children: React.ReactNode;
	/** URL to redirect to when the user is not authenticated. Defaults to `/login`. */
	loginUrl?: string;
}

/**
 * Protects a route tree behind authentication.
 *
 * While the auth state is loading, renders a centered spinner. Once
 * resolved, unauthenticated users are redirected to the login page
 * via a full-page navigation. Authenticated users see `children`.
 *
 * @example
 * ```tsx
 * <AuthGuard>
 *   <DashboardLayout />
 * </AuthGuard>
 * ```
 */
function AuthGuard({ children, loginUrl = '/login' }: AuthGuardProps) {
	const { isAuthenticated, isLoading, isReady } = useAuth();

	// Redirect unauthenticated users to the login page. We use an
	// effect + window.location because /login lives in the web shell
	// (a separate Vite application).
	useEffect(() => {
		if (isReady && !isAuthenticated) {
			// Preserve the intended destination so the login flow can
			// redirect back after successful authentication.
			const returnTo = window.location.pathname + window.location.search;
			const separator = loginUrl.includes('?') ? '&' : '?';
			window.location.href = `${loginUrl}${separator}returnTo=${encodeURIComponent(returnTo)}`;
		}
	}, [isReady, isAuthenticated, loginUrl]);

	if (isLoading || !isReady) {
		return <AuthLoading />;
	}

	if (!isAuthenticated) {
		// Render the loading screen while the redirect effect fires.
		return <AuthLoading />;
	}

	return <>{children}</>;
}

// ---------------------------------------------------------------------------
// ProfileGuard
// ---------------------------------------------------------------------------

interface ProfileGuardProps {
	children: React.ReactNode;
	/**
	 * URL to redirect to when the user has not completed their profile.
	 * Defaults to `/onboard/profile`. The current pathname + search is
	 * appended as a `returnTo` query parameter so the user can be sent
	 * back to the originally-requested URL after completion.
	 */
	profileUrl?: string;
}

/**
 * Protects a route tree behind a complete profile (`name` + `birthday`).
 *
 * Should be composed **inside** an {@link AuthGuard} so the user is
 * guaranteed to be authenticated by the time this guard runs.
 *
 * While loading, renders a spinner. If the authenticated user has not
 * yet set their `name` and `birthday`, performs a cross-zone redirect
 * to `/onboard/profile?returnTo=<current path>`. The onboarding profile
 * page reads `returnTo` and navigates the user back to the original URL
 * once they finish, preserving deep-links such as
 * `/onboard/join?code=ABCXYZ123`.
 *
 * @example
 * ```tsx
 * <AuthGuard>
 *   <ProfileGuard>
 *     <ScopeGuard scope="admin">
 *       <AdminLayout />
 *     </ScopeGuard>
 *   </ProfileGuard>
 * </AuthGuard>
 * ```
 */
function ProfileGuard({ children, profileUrl = '/onboard/profile' }: ProfileGuardProps) {
	const { isAuthenticated, profileComplete, isLoading, isReady } = useAuth();

	useEffect(() => {
		if (!isReady || isLoading) return;
		if (!isAuthenticated) return;
		if (profileComplete) return;

		// Preserve the intended destination so the profile page can
		// redirect back after successful completion.
		const returnTo = window.location.pathname + window.location.search;
		const separator = profileUrl.includes('?') ? '&' : '?';
		window.location.href = `${profileUrl}${separator}returnTo=${encodeURIComponent(returnTo)}`;
	}, [isReady, isLoading, isAuthenticated, profileComplete, profileUrl]);

	if (isLoading || !isReady) {
		return <AuthLoading />;
	}

	if (isAuthenticated && !profileComplete) {
		// Render the loading screen while the redirect effect fires.
		return <AuthLoading />;
	}

	return <>{children}</>;
}

// ---------------------------------------------------------------------------
// ScopeGuard
// ---------------------------------------------------------------------------

interface ScopeGuardProps {
	children: React.ReactNode;
	/** The scope required to render the children. */
	scope: AuthScope;
}

/**
 * Protects a route tree behind a specific access scope.
 *
 * Should be composed **inside** an {@link AuthGuard} so the user is
 * guaranteed to be authenticated by the time this guard runs.
 *
 * While loading, renders a spinner. If the user lacks the required
 * scope, they are redirected to their allowed zone via
 * {@link defaultRedirectForScopes} — admin users land on `/admin`,
 * student users on `/learn`, and users with no scopes on `/onboard`.
 *
 * @example
 * ```tsx
 * <AuthGuard>
 *   <ScopeGuard scope="admin">
 *     <AdminLayout />
 *   </ScopeGuard>
 * </AuthGuard>
 * ```
 */
function ScopeGuard({ children, scope }: ScopeGuardProps) {
	const { hasScope, scopes, isLoading, isReady } = useAuth();

	// Redirect users who lack the required scope to their allowed zone.
	// Uses window.location.href because each zone is a separate SPA.
	useEffect(() => {
		if (isReady && !isLoading && !hasScope(scope)) {
			window.location.href = defaultRedirectForScopes(scopes);
		}
	}, [isReady, isLoading, hasScope, scope, scopes]);

	if (isLoading || !isReady) {
		return <AuthLoading />;
	}

	if (!hasScope(scope)) {
		// Render the loading screen while the redirect effect fires.
		return <AuthLoading />;
	}

	return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { AuthGuard, AuthLoading, ProfileGuard, safeReturnTo, ScopeGuard };
export type { AuthGuardProps, ProfileGuardProps, ScopeGuardProps };
